// Sharing (contracts/index-cards.md §2–§5, admin-api.md "Sharing"): the Logic Index tiers with their crest tiles,
// friendly names for share kinds, link kinds, formats, channels, platforms, in-app browsers and sources, and the
// index.* / share.* config keys as backend/api validates them. Shared by the pages and the mock (src/mocks/db.ts
// validates PUT /config with these schemas; src/mocks/sharing.ts builds its data with the tiers).

import { z } from "zod";

// ---------------------------------------------------------------- tiers

export const TIER_IDS = ["spark", "beam", "bright", "radiant", "brilliant", "luminary"] as const;
export type TierId = (typeof TIER_IDS)[number];

/**
 * Every value lies in 65–135 (index-cards.md §7), so Spark starts at 65 and Luminary ends at 135. `crest` is the
 * tier's crest tile (§2); `tile` the emissive tile class the panel's KPI tiles have for it (tiles.css has no accent
 * tile, so Luminary's laser yellow takes the yellow moss tile).
 */
export const TIERS: readonly { id: TierId; name: string; min: number; max: number; crest: string; tile: string }[] = [
  { id: "spark", name: "Spark", min: 65, max: 84, crest: "ember", tile: "ember" },
  { id: "beam", name: "Beam", min: 85, max: 99, crest: "teal", tile: "teal" },
  { id: "bright", name: "Bright", min: 100, max: 109, crest: "azure", tile: "azure" },
  { id: "radiant", name: "Radiant", min: 110, max: 119, crest: "magenta", tile: "magenta" },
  { id: "brilliant", name: "Brilliant", min: 120, max: 129, crest: "violet", tile: "violet" },
  { id: "luminary", name: "Luminary", min: 130, max: 135, crest: "accent", tile: "moss" },
];

/** A tier's crest colour (a token) for chart series and crest marks; unknown tiers are neutral. */
export const TIER_COLOR: Record<TierId, string> = {
  spark: "var(--color-state-ember)",
  beam: "var(--color-state-teams)",
  bright: "var(--color-avatar-orbs-1-1)",
  radiant: "var(--color-avatar-orbs-4-1)",
  brilliant: "var(--color-state-violet)",
  luminary: "var(--color-accent)",
};
export const tierColor = (id: string | null | undefined) => (id && id in TIER_COLOR ? TIER_COLOR[id as TierId] : "var(--color-text-quaternary)");
export const tierName = (id: string | null | undefined) => TIERS.find((t) => t.id === id)?.name ?? (id ? id[0]!.toUpperCase() + id.slice(1) : "—");
export const tierOf = (value: number) => TIERS.find((t) => value <= t.max) ?? TIERS[TIERS.length - 1]!;

/** The population's 14 bins (§7): 65–69, 70–74 … 125–129, and 130–135; each lies in one tier. */
export const BINS: readonly { from: number; to: number }[] = [
  ...Array.from({ length: 13 }, (_, i) => ({ from: 65 + i * 5, to: 69 + i * 5 })),
  { from: 130, to: 135 },
];

// ---------------------------------------------------------------- vocabulary

export const SHARE_KIND_LABEL: Record<string, string> = { index: "Logic Index", daily: "Daily result" };
export const LINK_KIND_LABEL: Record<string, string> = {
  index_share: "Index share",
  daily_share: "Daily share",
  invite: "Friend invite",
  team: "Team invite",
  duel: "Duel",
  room: "Room",
};
export const FORMAT_LABEL: Record<string, string> = { story: "Story", reel: "Reel", post: "Post", square: "Square", text: "Text" };
export const CHANNEL_LABEL: Record<string, string> = {
  copy: "Copy link",
  messages: "Messages",
  whatsapp: "WhatsApp",
  instagram: "Instagram",
  tiktok: "TikTok",
  x: "X",
  facebook: "Facebook",
  snapchat: "Snapchat",
  save: "Saved to Photos",
  other: "Other",
};
export const PLATFORM_LABEL: Record<string, string> = { ios: "iOS", android: "Android", desktop: "Desktop", other: "Other" };
export const IN_APP_LABEL: Record<string, string> = {
  instagram: "Instagram",
  tiktok: "TikTok",
  facebook: "Facebook",
  snapchat: "Snapchat",
  x: "X",
  none: "None (a browser)",
};
export const SOURCE_LABEL: Record<string, string> = {
  instagram: "Instagram",
  tiktok: "TikTok",
  facebook: "Facebook",
  x: "X",
  whatsapp: "WhatsApp",
  google: "Google",
  direct: "Direct",
  other: "Other",
};

/** The friendly name of a key, or the key itself (new values from the API still show). */
export const labelOf = (labels: Record<string, string>, key: string | null | undefined) => (key ? (labels[key] ?? key.replace(/_/g, " ")) : "—");

/** The panel's page for one share (the public link, `/s/<id>` or `/<n>?r=<id>`, comes from the API as `url`). */
export const sharePath = (kind: string, ref: string) => `/sharing/shares/${encodeURIComponent(kind)}/${encodeURIComponent(ref)}`;

// ---------------------------------------------------------------- config (index-cards.md §1, §4, §5)

/** The app's App Store id (the web's fallback when it has no APP_STORE_URL, §7). */
export const APP_STORE_ID = "6816461498";
/** The public App Store page (the web's APP_STORE_URL). */
export const APP_STORE_URL = "https://apps.apple.com/us/app/iq-daily-brain-games-test/id6816461498";
/** The web's App Store link (§4): with a provider token, `pt` and `ct` (`<kind>-<format>` on share pages). */
export const campaignLink = (token: string | null, ct = "index-story") =>
  token ? `https://apps.apple.com/app/apple-store/id${APP_STORE_ID}?pt=${token}&ct=${ct}&mt=8` : APP_STORE_URL;

export const PROVIDER_TOKEN_RE = /^\d{1,20}$/;
export const MIN_PLAYERS = { min: 1, max: 100_000 } as const;

export const AppStoreCampaign = z.object({ providerToken: z.string().regex(PROVIDER_TOKEN_RE, "the provider token is 1–20 digits").nullable() });
export type AppStoreCampaign = z.infer<typeof AppStoreCampaign>;

export const SHARING_CONFIG = {
  /** Free players see their Logic Index (false: Pro only, as before). */
  "index.free": { schema: z.boolean(), default: true },
  /** Below this many index holders the standing and the curve are hidden from players. */
  "index.minPlayers": { schema: z.int().min(MIN_PLAYERS.min).max(MIN_PLAYERS.max), default: 30 },
  /** The App Store Connect provider token for campaign links (`pt=`); null: plain store links. */
  "share.appStoreCampaign": { schema: AppStoreCampaign, default: { providerToken: null } as AppStoreCampaign },
} as const;

export function readSharingConfig(values: Record<string, unknown> | undefined) {
  const get = <K extends keyof typeof SHARING_CONFIG>(k: K) => {
    const parsed = SHARING_CONFIG[k].schema.safeParse(values?.[k]);
    return (parsed.success ? parsed.data : SHARING_CONFIG[k].default) as z.infer<(typeof SHARING_CONFIG)[K]["schema"]>;
  };
  return { free: get("index.free"), minPlayers: get("index.minPlayers"), campaign: get("share.appStoreCampaign") };
}
export type SharingConfig = ReturnType<typeof readSharingConfig>;

/** The settings form's problems, in the API's terms. */
export function sharingConfigProblems(form: { minPlayers: string; providerToken: string }): string[] {
  const n = Number(form.minPlayers);
  const token = form.providerToken.trim();
  return [
    ...(!form.minPlayers.trim() || !Number.isInteger(n) || n < MIN_PLAYERS.min || n > MIN_PLAYERS.max ? [`The minimum is a whole number from ${MIN_PLAYERS.min} to ${MIN_PLAYERS.max.toLocaleString("en-US")}.`] : []),
    ...(token && !PROVIDER_TOKEN_RE.test(token) ? ["The provider token is 1 to 20 digits (App Store Connect → App Analytics → Campaigns)."] : []),
  ];
}
